import React, { useState, useEffect } from 'react';
import { useConsent } from '../contexts/ConsentContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Shield, ChevronRight, Check } from 'lucide-react';

export const ConsentBanner: React.FC = () => {
  const {
    showBanner,
    showSettings,
    consentState,
    openSettings,
    closeSettings,
    acceptAll,
    rejectAll,
    saveConsent,
    consentDecision,
  } = useConsent();

  const { language } = useLanguage();
  const isDe = language === 'DE';

  // Local state for the settings checkbox inputs
  const [localAnalytics, setLocalAnalytics] = useState(consentState.analytics);
  const [localMarketing, setLocalMarketing] = useState(consentState.marketing);

  // Sync local checkboxes with global state when settings modal is opened
  useEffect(() => {
    if (showSettings) {
      setLocalAnalytics(consentState.analytics);
      setLocalMarketing(consentState.marketing);
    }
  }, [showSettings, consentState]);

  if (!showBanner) return null;

  const handleSave = () => {
    saveConsent({
      analytics: localAnalytics,
      marketing: localMarketing,
    });
  };

  const privacyUrl = isDe ? 'https://pstax.at/de/datenschutz/' : 'https://pstax.at/en/privacy-policy/';
  const privacyText = isDe ? 'Datenschutzerklärung' : 'Privacy Policy';

  return (
    <div className="fixed inset-0 z-[99999] flex items-end justify-center pointer-events-none p-2 sm:p-4 md:p-6 bg-transparent">
      {/* Backdrop for Settings Modal */}
      {showSettings && (
        <div 
          className="fixed inset-0 bg-pstax-navy/40 backdrop-blur-xs pointer-events-auto transition-opacity"
          onClick={closeSettings}
        />
      )}

      {/* Main Consent Panel Card */}
      <div
        className={`relative z-10 w-full bg-white border border-pstax-border shadow-2xl rounded-xl sm:rounded-2xl pointer-events-auto flex flex-col transition-all duration-300 ${
          showSettings 
            ? 'max-w-lg self-center mb-0 md:mb-12 max-h-[85vh]' 
            : 'max-w-lg sm:max-w-2xl md:max-w-3xl lg:max-w-4xl mb-1 sm:mb-2 md:mb-4'
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Cookie Consent Banner"
      >
        {!showSettings ? (
          /* Sleek Minimalist Banner Layout - Compact Sizes */
          <div className="p-3 sm:p-4 md:p-5 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 md:gap-5">
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mb-1">
                <Shield className="text-pstax-blue shrink-0" size={16} />
                <h4 className="font-display font-semibold text-pstax-navy text-xs sm:text-xs md:text-sm tracking-tight">
                  {isDe ? 'GTM & Cookie-Einwilligung' : 'GTM & Cookie Consent'}
                </h4>
                <span className="text-gray-300 hidden sm:inline">•</span>
                <a 
                  href={privacyUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-[10px] sm:text-xs font-bold text-pstax-blue hover:underline inline-flex items-center"
                  id="banner-privacy-link"
                >
                  {privacyText}
                </a>
              </div>
              <p className="text-[10px] sm:text-xs text-pstax-gray leading-normal">
                {isDe
                  ? 'Wir verwenden notwendige Technologien für den Betrieb der Website sowie optional Statistik- und Marketing-/Externe-Medien-Dienste, um unsere Website zu verbessern und Terminbuchungen über Calendly zu ermöglichen.'
                  : 'We use necessary technologies to operate this website and optional analytics and marketing/external media services to improve our website and enable appointment booking via Calendly.'
                }
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 shrink-0 w-full lg:w-auto">
              <button
                onClick={openSettings}
                className="sm:flex-1 lg:flex-none min-h-[44px] py-2.5 px-4 text-xs font-semibold text-pstax-navy bg-pstax-bg hover:bg-pstax-border border border-pstax-border rounded-lg transition-all"
                id="consent-settings-btn"
              >
                {isDe ? 'Einstellungen' : 'Settings'}
              </button>
              <button
                onClick={acceptAll}
                className="sm:flex-[1.5] lg:flex-none min-h-[44px] py-2.5 px-5 text-xs font-semibold bg-pstax-navy hover:bg-pstax-navy/90 text-white rounded-lg transition-all shadow-md shadow-pstax-navy/5"
                id="consent-accept-all-btn"
              >
                {isDe ? 'Akzeptieren' : 'Accept'}
              </button>
            </div>
          </div>
        ) : (
          /* Detailed Granular Preferences View */
          <div className="flex flex-col h-full overflow-hidden max-h-[90vh]">
            {/* Header */}
            <div className="p-5 sm:p-6 border-b border-pstax-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="text-pstax-navy shrink-0" size={20} />
                <h3 className="font-display font-bold text-pstax-navy text-base sm:text-lg">
                  {isDe ? 'Cookie-Einstellungen' : 'Cookie Settings'}
                </h3>
              </div>
              {consentDecision && (
                <button 
                  onClick={closeSettings}
                  className="text-pstax-navy hover:bg-slate-100 text-xs font-bold px-2.5 py-1 rounded-md border border-slate-200 transition-colors"
                >
                  {isDe ? 'Schließen' : 'Close'}
                </button>
              )}
            </div>

            {/* Scrollable Settings List */}
            <div className="p-5 sm:p-6 space-y-5 overflow-y-auto flex-1">
              <p className="text-xs sm:text-sm text-slate-700 leading-normal font-normal">
                {isDe 
                  ? 'Passe deine Einstellungen für externe Dienste und Cookies hier an. Mehr Informationen findest du in unserer '
                  : 'Adjust your preferences for external tools and cookies here. Learn more in our '}
                <a href={privacyUrl} target="_blank" rel="noopener noreferrer" className="text-pstax-navy hover:text-blue-700 font-bold underline underline-offset-2">
                  {privacyText}
                </a>.
              </p>

              <div className="space-y-3.5">
                {/* 1. Necessary (Notwendig) */}
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-[11px] bg-slate-200 text-slate-800 py-0.5 px-2 rounded uppercase tracking-wide">
                        {isDe ? 'Aktiv' : 'Active'}
                      </span>
                      <h5 className="font-display font-bold text-sm text-pstax-navy">
                        {isDe ? 'Notwendig' : 'Necessary'}
                      </h5>
                    </div>
                    <p className="text-xs text-slate-700 leading-relaxed font-normal">
                      {isDe 
                        ? 'Erforderlich für den Betrieb der Website. Diese Kategorie kann nicht deaktiviert werden.'
                        : 'Required to operate the website. This category cannot be disabled.'
                      }
                    </p>
                  </div>
                  <div className="relative inline-flex items-center h-6 shrink-0 mt-1">
                    <input
                      type="checkbox"
                      disabled
                      checked
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-pstax-navy/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pstax-navy opacity-70"></div>
                  </div>
                </div>

                {/* 2. Analytics (Statistik) */}
                <label className="p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 flex items-start justify-between gap-4 cursor-pointer transition-colors block">
                  <div className="flex-1">
                    <h5 className="font-display font-bold text-sm text-pstax-navy mb-1">
                      {isDe ? 'Statistik' : 'Analytics'}
                    </h5>
                    <p className="text-xs text-slate-700 leading-relaxed font-normal">
                      {isDe
                        ? 'Hilft uns zu verstehen, wie Besucher die Website und den Fit Check nutzen. Dazu gehören Google Analytics und Microsoft Clarity.'
                        : 'Helps us understand how visitors use the website and the Fit Check. This includes Google Analytics and Microsoft Clarity.'
                      }
                    </p>
                  </div>
                  <div className="relative inline-flex items-center h-6 shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={localAnalytics}
                      onChange={(e) => setLocalAnalytics(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pstax-navy"></div>
                  </div>
                </label>

                {/* 3. Marketing/External Media (Marketing/Externe Medien) */}
                <label className="p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 flex items-start justify-between gap-4 cursor-pointer transition-colors block">
                  <div className="flex-1">
                    <h5 className="font-display font-bold text-sm text-pstax-navy mb-1">
                      {isDe ? 'Marketing / Externe Medien' : 'Marketing / External media'}
                    </h5>
                    <p className="text-xs text-slate-700 leading-relaxed font-normal">
                      {isDe 
                        ? 'Erlaubt externe Dienste und Marketingfunktionen, insbesondere Calendly für die Terminbuchung und Ads-/Remarketing-Funktionen (Meta Pixel).'
                        : 'Allows external services and marketing features, especially Calendly for appointment booking and ads/remarketing features (Meta Pixel).'
                      }
                    </p>
                  </div>
                  <div className="relative inline-flex items-center h-6 shrink-0 mt-1">
                    <input
                      type="checkbox"
                      checked={localMarketing}
                      onChange={(e) => setLocalMarketing(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pstax-navy"></div>
                  </div>
                </label>
              </div>
            </div>

            {/* Actions Footer - High contrast with Accept All, Save Selection, Reject All */}
            <div className="p-4 sm:p-6 border-t border-pstax-border bg-pstax-bg/50 flex flex-col sm:flex-row gap-2.5 sm:gap-3">
              <button
                type="button"
                onClick={rejectAll}
                className="w-full sm:flex-1 py-2.5 sm:py-3 px-3 text-xs font-bold text-slate-700 hover:text-pstax-navy bg-white hover:bg-slate-100 border border-slate-300 rounded-xl transition-all"
                id="modal-reject-all-btn"
              >
                {isDe ? 'Alle ablehnen' : 'Reject All'}
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="w-full sm:flex-1 py-2.5 sm:py-3 px-3 text-xs font-bold text-pstax-navy bg-white hover:bg-slate-100 border border-slate-300 hover:border-pstax-navy rounded-xl transition-all"
                id="modal-save-selection-btn"
              >
                {isDe ? 'Auswahl speichern' : 'Save Selection'}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="w-full sm:flex-1 py-2.5 sm:py-3 px-3 text-xs font-bold bg-pstax-navy hover:bg-pstax-navy/90 text-white rounded-xl transition-all shadow-md"
                id="modal-accept-all-btn"
              >
                {isDe ? 'Alle akzeptieren' : 'Accept All'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
